import { X, ArrowUpRight, ArrowDownRight, Clock, Hash, Cpu, TrendingUp, DollarSign } from 'lucide-react';

const DEFAULT_FEE_RATE = 0.0125; // 1.25% per side (2.5% round-trip floor)

export const TradeDetailModal = ({ trade, onClose, feeRate }) => {
  if (!trade) return null;

  const isBuy = (trade.type || '').toUpperCase() === 'BUY';
  const isLive = trade.mode === 'live';
  const coin = (trade.pair || '').split('-')[0];
  const price = Number(trade.price || 0);
  const amount = Number(trade.amount || 0);
  const usdValue = Number(trade.usd_value || 0);
  const profit = trade.profit != null ? Number(trade.profit) : null;
  const rsi = trade.rsi != null ? Number(trade.rsi) : null;
  const actualFee = trade.fee != null ? Number(trade.fee) : null;

  const rate = feeRate || DEFAULT_FEE_RATE;
  const feePct = (rate * 100).toFixed(2);

  // Estimate fees if not recorded
  const estFee = actualFee != null ? actualFee : usdValue * rate;

  // For sells, estimate the round-trip
  const estBuyFee = !isBuy ? usdValue * rate : null;
  const estSellFee = !isBuy ? usdValue * rate : null;
  const totalEstFees = !isBuy ? (estBuyFee + estSellFee) : estFee;

  // For sells, compute implied buy price from cost basis if possible
  // profit = (sell_price - buy_price) * amount  →  buy_price = sell_price - (profit / amount)
  const impliedBuyPrice =
    !isBuy && profit != null && amount > 0 ? price - profit / amount : null;

  const ts = trade.timestamp ? new Date(trade.timestamp) : null;
  const fullTime = ts ? ts.toLocaleString([], { 
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) : 'N/A';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="trade-detail-overlay">
      <div 
        className="relative w-full max-w-md mx-4 border bg-[#0a0a0a] border-[#003B00] shadow-[0_0_30px_rgba(0,255,65,0.1)] max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="trade-detail-modal"
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isBuy ? 'border-green-800/50 bg-green-950/30' : 'border-red-800/50 bg-red-950/30'} sticky top-0 z-10`}>
          <div className="flex items-center gap-2">
            {isBuy ? <ArrowDownRight className="h-4 w-4 text-green-400" /> : <ArrowUpRight className="h-4 w-4 text-red-400" />}
            <span className={`text-sm font-bold uppercase tracking-wider ${isBuy ? 'text-green-400' : 'text-red-400'}`}>
              {isBuy ? 'BUY' : 'SELL'} {coin}
            </span>
            <span className={`text-[8px] font-bold px-1.5 py-0.5 uppercase ${
              isLive ? 'text-orange-400 border border-orange-800/50' : 'text-blue-400 border border-blue-800/50'
            }`}>
              {isLive ? 'LIVE' : 'SIM'}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="trade-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3">
          {/* Time */}
          <Row icon={<Clock className="h-3.5 w-3.5" />} label="Time" value={fullTime} />

          {/* Price & Amount */}
          <div className="grid grid-cols-2 gap-3">
            <Row
              icon={<DollarSign className="h-3.5 w-3.5" />}
              label={isBuy ? 'Buy Price' : 'Sell Price'}
              value={`$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: price < 1 ? 8 : 2 })}`}
            />
            <Row icon={<Hash className="h-3.5 w-3.5" />} label="Amount" value={`${amount.toFixed(8)} ${coin}`} />
          </div>

          {/* Implied buy price (sells only) */}
          {!isBuy && impliedBuyPrice != null && impliedBuyPrice > 0 && (
            <Row
              icon={<DollarSign className="h-3.5 w-3.5" />}
              label="Avg Buy Price (cost basis)"
              value={`$${impliedBuyPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: impliedBuyPrice < 1 ? 8 : 2 })}`}
            />
          )}

          {/* USD Value */}
          <Row 
            icon={<DollarSign className="h-3.5 w-3.5" />} 
            label={isBuy ? 'Total Cost' : 'Proceeds'} 
            value={`$${usdValue.toFixed(2)}`} 
            highlight
          />

          {/* Fees Section */}
          <div className="border border-[#003B00]/50 bg-[#050505] p-2.5 space-y-1.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">Fee Breakdown</span>
            {isBuy ? (
              <Row label={`Est. Buy Fee (~${feePct}%)`} value={`$${estFee.toFixed(2)}`} sub />
            ) : (
              <>
                <Row label={`Est. Buy Fee (~${feePct}%)`} value={`$${estBuyFee.toFixed(2)}`} sub />
                <Row label={`Est. Sell Fee (~${feePct}%)`} value={`$${estSellFee.toFixed(2)}`} sub />
                {actualFee != null && (
                  <Row label="Actual Sell Fee" value={`$${actualFee.toFixed(2)}`} sub accent />
                )}
                <div className="border-t border-[#003B00]/30 pt-1">
                  <Row label="Est. Round-Trip Fees" value={`$${totalEstFees.toFixed(2)}`} sub bold />
                </div>
              </>
            )}
          </div>

          {/* Profit/Loss - only for sells */}
          {!isBuy && profit != null && (
            <div className={`p-2.5 border ${profit >= 0 ? 'border-green-800/50 bg-green-950/20' : 'border-red-800/50 bg-red-950/20'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Net Profit / Loss</span>
                <span className={`text-base font-bold font-mono ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {profit >= 0 ? '+' : ''}{profit.toFixed(4)}
                  <span className="text-[10px] ml-1">USD</span>
                </span>
              </div>
              {usdValue > 0 && (
                <div className="flex justify-end mt-0.5">
                  <span className={`text-[10px] font-mono ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ({profit >= 0 ? '+' : ''}{((profit / usdValue) * 100).toFixed(2)}%)
                  </span>
                </div>
              )}
            </div>
          )}

          {/* RSI & Trigger */}
          <div className="grid grid-cols-2 gap-3">
            {rsi != null && (
              <Row icon={<TrendingUp className="h-3.5 w-3.5" />} label="RSI" value={rsi.toFixed(1)} />
            )}
            <Row icon={<Cpu className="h-3.5 w-3.5" />} label="Trigger" value={trade.trigger || 'N/A'} />
          </div>

          {/* Per-lot breakdown (v3.9.6 protection sells) */}
          {!isBuy && Array.isArray(trade.lots_breakdown) && trade.lots_breakdown.length > 0 && (
            <div className="border border-[#003B00]/50 bg-[#050505] p-2.5 space-y-1.5">
              <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">
                Per-Lot Breakdown ({trade.lots_breakdown.length} lot{trade.lots_breakdown.length === 1 ? '' : 's'} sold)
              </span>
              <div className="space-y-1">
                {trade.lots_breakdown.map((lot, idx) => (
                  <div key={idx} className="flex items-center justify-between text-[10px] font-mono">
                    <span className="text-gray-500">
                      @ ${Number(lot.buy_price).toFixed(price < 1 ? 6 : 2)} ({Number(lot.amount).toFixed(8)})
                    </span>
                    <span className={(Number(lot.gross_profit) - Number(lot.fee_est)) >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {(Number(lot.gross_profit) - Number(lot.fee_est)) >= 0 ? '+' : ''}
                      ${(Number(lot.gross_profit) - Number(lot.fee_est)).toFixed(4)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Order ID / Exchange for live trades */}
          {isLive && (
            <div className="border-t border-[#003B00]/30 pt-2 space-y-1.5">
              {trade.order_id && (
                <Row label="Order ID" value={trade.order_id.slice(0, 16) + '...'} sub mono />
              )}
              {trade.exchange && (
                <Row label="Exchange" value={trade.exchange.toUpperCase()} sub />
              )}
              {trade.synced_from_exchange && (
                <span className="text-[9px] text-yellow-600 uppercase">Synced from exchange</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ icon, label, value, highlight, sub, bold, accent, mono }) => (
  <div className={`flex items-center justify-between ${sub ? 'px-0' : ''}`}>
    <div className="flex items-center gap-1.5">
      {icon && <span className="text-gray-600">{icon}</span>}
      <span className={`${sub ? 'text-[10px] text-gray-500' : 'text-[10px] text-gray-400 uppercase tracking-wider'} ${bold ? 'font-bold text-gray-300' : ''}`}>
        {label}
      </span>
    </div>
    <span className={`${sub ? 'text-[11px]' : 'text-[11px]'} ${highlight ? 'text-[#00FF41] font-bold' : accent ? 'text-yellow-400' : 'text-gray-200'} ${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : ''}`}>
      {value}
    </span>
  </div>
);

export default TradeDetailModal;
